/**
 * Issues Showcase
 *
 * Manages toggle between covers grid view and draggable cards view
 */

class IssuesShowcase {
  constructor(container) {
    console.log('[IssuesShowcase] Initializing...');

    this.container = container;
    this.coversView = container.querySelector('.issues-showcase__covers-view');
    this.cardsView = container.querySelector('.issues-showcase__cards-view');
    this.backButton = container.querySelector('.issues-showcase__back-button');
    this.coverCards = container.querySelectorAll('.issues-showcase__cover-card');
    this.draggableCards = container.querySelectorAll('.draggable-card');
    this.draggableContainer = container.querySelector('.draggable-media-container');

    this.currentView = 'covers'; // 'covers' or 'cards'
    this.selectedBlog = null;
    this.isMobile = window.innerWidth < 750;

    // Draggable functionality properties
    this.activeCard = null;
    this.initialX = 0;
    this.initialY = 0;
    this.currentX = 0;
    this.currentY = 0;

    // Mobile swipe properties
    this.mobileStack = [];
    this.currentCardIndex = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchCurrentX = 0;
    this.touchCurrentY = 0;
    this.isSwiping = false;
    this.swipeThreshold = 100; // pixels to trigger swipe
    this.cardCounter = container.querySelector('.mobile-card-counter');

    // Bound handlers for cleanup
    this.boundHandlers = {
      backClick: this.handleBackClick.bind(this),
      dragStart: this.dragStart.bind(this),
      drag: this.drag.bind(this),
      dragEnd: this.dragEnd.bind(this),
      resize: this.handleResize.bind(this),
      touchStart: this.handleTouchStart.bind(this),
      touchMove: this.handleTouchMove.bind(this),
      touchEnd: this.handleTouchEnd.bind(this)
    };

    this.initialize();
  }

  initialize() {
    console.log('[IssuesShowcase] Setting up event listeners...');

    // Cover card images now link directly to blog pages - no JavaScript handling needed

    // Add back button handler
    if (this.backButton) {
      this.backButton.addEventListener('click', this.boundHandlers.backClick);
    }

    // Initialize blog filter
    this.initializeBlogFilter();

    // Add resize handler
    window.addEventListener('resize', this.boundHandlers.resize);

    console.log('[IssuesShowcase] Initialization complete');
  }

  /**
   * Handle back button click - return to covers view
   */
  handleBackClick(event) {
    event.preventDefault();
    console.log('[IssuesShowcase] Back button clicked');
    this.showCoversView();
  }

  /**
   * Show covers grid view, hide cards view
   */
  showCoversView() {
    console.log('[IssuesShowcase] Switching to covers view');

    this.currentView = 'covers';

    // Update view visibility
    this.coversView.classList.remove('is-hidden');
    this.cardsView.classList.remove('is-visible');

    // Hide cards view
    setTimeout(() => {
      if (this.cardsView) {
        this.cardsView.style.display = 'none';
      }
    }, 400); // Match CSS transition duration

    // Hide back button
    if (this.backButton) {
      this.backButton.style.display = 'none';
    }

    // Reset filter to "all"
    this.resetBlogFilter();

    // Remove dragging or mobile swipe functionality
    if (!this.isMobile) {
      this.disableDragging();
    } else {
      this.disableMobileSwipe();
    }
  }

  /**
   * Show draggable cards view, hide covers grid
   */
  showCardsView(blogHandle) {
    console.log(`[IssuesShowcase] Switching to cards view, filtering by: ${blogHandle}`);

    this.currentView = 'cards';

    // Show cards view
    if (this.cardsView) {
      this.cardsView.style.display = 'block';

      // Trigger reflow
      this.cardsView.offsetHeight;

      // Add visible class for animation
      setTimeout(() => {
        this.cardsView.classList.add('is-visible');
      }, 10);
    }

    // Hide covers view
    this.coversView.classList.add('is-hidden');

    // Show back button
    if (this.backButton) {
      this.backButton.style.display = 'inline-flex';
    }

    // Filter cards by selected blog
    this.filterByBlog(blogHandle);

    // Initialize draggable functionality on desktop or mobile stack on mobile
    if (!this.isMobile) {
      // Wait for view transition to complete
      setTimeout(() => {
        this.initializeCardPositions();
        this.enableDragging();
      }, 450);
    } else {
      // Initialize mobile stack
      setTimeout(() => {
        this.initializeMobileStack();
        this.setupMobileSwipe();
      }, 450);
    }
  }

  /**
   * Initialize card positions randomly on the canvas
   */
  initializeCardPositions() {
    if (this.isMobile || !this.draggableContainer) return;

    console.log('[IssuesShowcase] Initializing card positions...');

    // Get visible (non-hidden) cards for the selected blog
    const visibleCards = Array.from(this.draggableCards).filter(card =>
      !card.classList.contains('draggable-card--hidden')
    );

    console.log(`[IssuesShowcase] Positioning ${visibleCards.length} visible cards`);

    const containerRect = this.draggableContainer.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = Math.max(800, containerRect.height);

    // Update container height
    this.draggableContainer.style.minHeight = `${containerHeight}px`;

    const positionedCards = [];

    visibleCards.forEach((card, index) => {
      const cardWidth = 320;
      const cardHeight = card.offsetHeight || 400;

      const padding = 40;
      const maxX = Math.max(padding, containerWidth - cardWidth - padding);
      const maxY = Math.max(padding, containerHeight - cardHeight - padding);

      // Find best position with minimal overlap
      const position = this.findBestPosition(
        cardWidth,
        cardHeight,
        { minX: padding, maxX: maxX, minY: padding, maxY: maxY },
        positionedCards
      );

      // Apply position
      card.style.transform = `translate(${position.x}px, ${position.y}px)`;
      card.dataset.translateX = position.x;
      card.dataset.translateY = position.y;

      positionedCards.push({
        x: position.x,
        y: position.y,
        width: cardWidth,
        height: cardHeight
      });
    });
  }

  /**
   * Find best position for a card with minimal overlap
   */
  findBestPosition(cardWidth, cardHeight, positionRange, positionedCards) {
    const maxAttempts = 20;
    let bestPosition = null;
    let minOverlap = Infinity;

    if (positionedCards.length === 0) {
      return {
        x: Math.floor(Math.random() * (positionRange.maxX - positionRange.minX)) + positionRange.minX,
        y: Math.floor(Math.random() * (positionRange.maxY - positionRange.minY)) + positionRange.minY
      };
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const x = Math.floor(Math.random() * (positionRange.maxX - positionRange.minX)) + positionRange.minX;
      const y = Math.floor(Math.random() * (positionRange.maxY - positionRange.minY)) + positionRange.minY;

      let totalOverlap = 0;

      for (const placedCard of positionedCards) {
        const overlap = this.calculateOverlap(
          x, y, cardWidth, cardHeight,
          placedCard.x, placedCard.y, placedCard.width, placedCard.height
        );
        totalOverlap += overlap;
      }

      if (totalOverlap < minOverlap) {
        minOverlap = totalOverlap;
        bestPosition = { x, y };

        if (totalOverlap === 0) break;
      }
    }

    return bestPosition || {
      x: Math.floor(Math.random() * (positionRange.maxX - positionRange.minX)) + positionRange.minX,
      y: Math.floor(Math.random() * (positionRange.maxY - positionRange.minY)) + positionRange.minY
    };
  }

  /**
   * Calculate overlap area between two rectangles
   */
  calculateOverlap(x1, y1, w1, h1, x2, y2, w2, h2) {
    const overlapLeft = Math.max(x1, x2);
    const overlapTop = Math.max(y1, y2);
    const overlapRight = Math.min(x1 + w1, x2 + w2);
    const overlapBottom = Math.min(y1 + h1, y2 + h2);

    if (overlapLeft < overlapRight && overlapTop < overlapBottom) {
      return (overlapRight - overlapLeft) * (overlapBottom - overlapTop);
    }

    return 0;
  }

  /**
   * Enable dragging functionality
   */
  enableDragging() {
    if (this.isMobile) return;

    console.log('[IssuesShowcase] Enabling dragging...');

    this.draggableCards.forEach(card => {
      if (!card.classList.contains('draggable-card--hidden')) {
        card.addEventListener('mousedown', this.boundHandlers.dragStart);
        card.addEventListener('touchstart', this.boundHandlers.dragStart, { passive: false });
      }
    });

    document.addEventListener('mousemove', this.boundHandlers.drag);
    document.addEventListener('touchmove', this.boundHandlers.drag, { passive: false });
    document.addEventListener('mouseup', this.boundHandlers.dragEnd);
    document.addEventListener('touchend', this.boundHandlers.dragEnd);
  }

  /**
   * Disable dragging functionality
   */
  disableDragging() {
    console.log('[IssuesShowcase] Disabling dragging...');

    this.draggableCards.forEach(card => {
      card.removeEventListener('mousedown', this.boundHandlers.dragStart);
      card.removeEventListener('touchstart', this.boundHandlers.dragStart);
    });

    document.removeEventListener('mousemove', this.boundHandlers.drag);
    document.removeEventListener('touchmove', this.boundHandlers.drag);
    document.removeEventListener('mouseup', this.boundHandlers.dragEnd);
    document.removeEventListener('touchend', this.boundHandlers.dragEnd);
  }

  /**
   * Initialize mobile stack
   */
  initializeMobileStack() {
    if (!this.isMobile) return;

    console.log('[IssuesShowcase] Initializing mobile stack...');

    // Get visible (non-hidden) cards
    this.mobileStack = Array.from(this.draggableCards).filter(card =>
      !card.classList.contains('draggable-card--hidden')
    );

    console.log(`[IssuesShowcase] Mobile stack has ${this.mobileStack.length} cards`);

    this.currentCardIndex = 0;

    // Update stack positions
    this.updateStackPositions();

    // Update counter
    this.updateCardCounter();

    // Show counter
    if (this.cardCounter && this.mobileStack.length > 0) {
      this.cardCounter.style.display = 'block';
    }
  }

  /**
   * Setup mobile swipe functionality
   */
  setupMobileSwipe() {
    if (!this.isMobile || this.mobileStack.length === 0) return;

    console.log('[IssuesShowcase] Setting up mobile swipe...');

    // Get the top card
    const topCard = this.mobileStack[this.currentCardIndex];
    if (topCard) {
      topCard.addEventListener('touchstart', this.boundHandlers.touchStart, { passive: false });
      topCard.addEventListener('touchmove', this.boundHandlers.touchMove, { passive: false });
      topCard.addEventListener('touchend', this.boundHandlers.touchEnd);
    }
  }

  /**
   * Disable mobile swipe functionality
   */
  disableMobileSwipe() {
    console.log('[IssuesShowcase] Disabling mobile swipe...');

    this.mobileStack.forEach(card => {
      card.removeEventListener('touchstart', this.boundHandlers.touchStart);
      card.removeEventListener('touchmove', this.boundHandlers.touchMove);
      card.removeEventListener('touchend', this.boundHandlers.touchEnd);
    });

    // Hide counter
    if (this.cardCounter) {
      this.cardCounter.style.display = 'none';
    }
  }

  /**
   * Handle touch start
   */
  handleTouchStart(e) {
    if (!this.isMobile) return;

    const touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchCurrentX = touch.clientX;
    this.touchCurrentY = touch.clientY;
    this.isSwiping = false;

    const topCard = this.mobileStack[this.currentCardIndex];
    if (topCard) {
      topCard.classList.add('draggable-card--swiping');
    }
  }

  /**
   * Handle touch move
   */
  handleTouchMove(e) {
    if (!this.isMobile) return;

    e.preventDefault();

    const touch = e.touches[0];
    this.touchCurrentX = touch.clientX;
    this.touchCurrentY = touch.clientY;

    const deltaX = this.touchCurrentX - this.touchStartX;
    const deltaY = this.touchCurrentY - this.touchStartY;

    // Only start swiping if horizontal movement is greater than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      this.isSwiping = true;
    }

    if (this.isSwiping) {
      const topCard = this.mobileStack[this.currentCardIndex];
      if (topCard) {
        // Calculate rotation based on horizontal movement
        const rotation = (deltaX / 20);
        const verticalOffset = Math.abs(deltaX) / 10;

        topCard.style.transform = `translateX(calc(-50% + ${deltaX}px)) translateY(${verticalOffset}px) rotate(${rotation}deg)`;
      }
    }
  }

  /**
   * Handle touch end
   */
  handleTouchEnd(e) {
    if (!this.isMobile) return;

    const deltaX = this.touchCurrentX - this.touchStartX;
    const topCard = this.mobileStack[this.currentCardIndex];

    if (!topCard) return;

    topCard.classList.remove('draggable-card--swiping');

    // Check if swipe threshold was met (left or right)
    if (this.isSwiping && Math.abs(deltaX) >= this.swipeThreshold) {
      console.log(`[IssuesShowcase] Swipe detected: ${deltaX > 0 ? 'right' : 'left'}`);

      // Dismiss card with appropriate animation
      if (deltaX > 0) {
        this.dismissTopCard('right');
      } else {
        this.dismissTopCard('left');
      }
    } else {
      // Snap back to center
      topCard.style.transform = '';
    }

    // Reset tracking
    this.isSwiping = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchCurrentX = 0;
    this.touchCurrentY = 0;
  }

  /**
   * Dismiss top card with animation
   */
  dismissTopCard(direction) {
    console.log(`[IssuesShowcase] Dismissing top card (${direction})`);

    const topCard = this.mobileStack[this.currentCardIndex];
    if (!topCard) return;

    // Remove touch listeners from this card
    topCard.removeEventListener('touchstart', this.boundHandlers.touchStart);
    topCard.removeEventListener('touchmove', this.boundHandlers.touchMove);
    topCard.removeEventListener('touchend', this.boundHandlers.touchEnd);

    // Apply exit animation
    topCard.style.transform = '';
    topCard.classList.add(`draggable-card--swipe-${direction}`);

    // Mark as dismissed after animation
    setTimeout(() => {
      topCard.classList.add('draggable-card--dismissed');
      topCard.classList.remove(`draggable-card--swipe-${direction}`);
      topCard.removeAttribute('data-stack-position');

      // Move to next card
      this.currentCardIndex++;

      if (this.currentCardIndex < this.mobileStack.length) {
        // Update stack positions
        this.updateStackPositions();

        // Update counter
        this.updateCardCounter();

        // Setup swipe for new top card
        this.setupMobileSwipe();
      } else {
        // Reached end of stack - loop back to beginning
        console.log('[IssuesShowcase] End of stack reached, cycling back to start');
        this.resetMobileStack();
      }
    }, 400); // Match animation duration
  }

  /**
   * Reset mobile stack to beginning (for cycling)
   */
  resetMobileStack() {
    console.log('[IssuesShowcase] Resetting mobile stack...');

    // Reset all cards
    this.mobileStack.forEach(card => {
      card.classList.remove('draggable-card--dismissed');
      card.style.transform = '';
      card.style.zIndex = '';
    });

    // Reset index
    this.currentCardIndex = 0;

    // Re-initialize
    this.updateStackPositions();
    this.updateCardCounter();
    this.setupMobileSwipe();
  }

  /**
   * Update stack positions
   */
  updateStackPositions() {
    if (!this.isMobile) return;

    console.log('[IssuesShowcase] Updating stack positions...');

    this.mobileStack.forEach((card, index) => {
      if (index < this.currentCardIndex) {
        // Already dismissed
        return;
      }

      const stackPosition = index - this.currentCardIndex;

      if (stackPosition <= 3) {
        card.setAttribute('data-stack-position', stackPosition);
      } else {
        card.removeAttribute('data-stack-position');
      }
    });
  }

  /**
   * Update card counter
   */
  updateCardCounter() {
    if (!this.cardCounter) return;

    const currentElement = this.cardCounter.querySelector('.mobile-card-counter__current');
    const totalElement = this.cardCounter.querySelector('.mobile-card-counter__total');

    if (currentElement && totalElement) {
      currentElement.textContent = this.currentCardIndex + 1;
      totalElement.textContent = this.mobileStack.length;
    }

    console.log(`[IssuesShowcase] Counter updated: ${this.currentCardIndex + 1}/${this.mobileStack.length}`);
  }

  /**
   * Drag start handler
   */
  dragStart(e) {
    if (this.isMobile) return;

    e.preventDefault();

    this.activeCard = e.currentTarget;
    this.activeCard.classList.add('active');

    // Bring to front
    this.draggableCards.forEach(card => {
      if (card !== this.activeCard) {
        card.style.zIndex = '1';
      }
    });
    this.activeCard.style.zIndex = '10';

    if (e.type === 'touchstart') {
      this.initialX = e.touches[0].clientX - parseFloat(this.activeCard.dataset.translateX || 0);
      this.initialY = e.touches[0].clientY - parseFloat(this.activeCard.dataset.translateY || 0);
    } else {
      this.initialX = e.clientX - parseFloat(this.activeCard.dataset.translateX || 0);
      this.initialY = e.clientY - parseFloat(this.activeCard.dataset.translateY || 0);
    }
  }

  /**
   * Drag handler
   */
  drag(e) {
    if (this.isMobile || !this.activeCard) return;

    e.preventDefault();

    if (e.type === 'touchmove') {
      this.currentX = e.touches[0].clientX - this.initialX;
      this.currentY = e.touches[0].clientY - this.initialY;
    } else {
      this.currentX = e.clientX - this.initialX;
      this.currentY = e.clientY - this.initialY;
    }

    this.activeCard.dataset.translateX = this.currentX;
    this.activeCard.dataset.translateY = this.currentY;
    this.activeCard.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
  }

  /**
   * Drag end handler
   */
  dragEnd() {
    if (!this.activeCard) return;

    this.activeCard.classList.remove('active');
    this.activeCard = null;
  }

  /**
   * Initialize blog filter
   */
  initializeBlogFilter() {
    const filterLinks = this.container.querySelectorAll('.draggable-blog-filter__link');

    filterLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();

        // Update active state
        filterLinks.forEach(l => l.classList.remove('draggable-blog-filter__link--active'));
        link.classList.add('draggable-blog-filter__link--active');

        // Get blog handle and filter
        const blogHandle = link.dataset.blogHandle;
        this.filterByBlog(blogHandle);
      });
    });
  }

  /**
   * Filter cards by blog handle
   */
  filterByBlog(blogHandle) {
    console.log(`[IssuesShowcase] Filtering cards by: ${blogHandle}`);

    this.selectedBlog = blogHandle;

    this.draggableCards.forEach(card => {
      const cardBlogHandle = card.dataset.blogHandle;

      if (blogHandle === 'all' || cardBlogHandle === blogHandle) {
        card.classList.remove('draggable-card--hidden');
      } else {
        card.classList.add('draggable-card--hidden');
      }
    });

    // Re-initialize positions if in cards view
    if (this.currentView === 'cards') {
      if (!this.isMobile) {
        setTimeout(() => {
          this.initializeCardPositions();
        }, 100);
      } else {
        // Reinitialize mobile stack with new filter
        this.disableMobileSwipe();
        setTimeout(() => {
          this.initializeMobileStack();
          this.setupMobileSwipe();
        }, 100);
      }
    }
  }

  /**
   * Reset blog filter to "all"
   */
  resetBlogFilter() {
    const allLink = this.container.querySelector('.draggable-blog-filter__link[data-blog-handle="all"]');
    const filterLinks = this.container.querySelectorAll('.draggable-blog-filter__link');

    filterLinks.forEach(l => l.classList.remove('draggable-blog-filter__link--active'));

    if (allLink) {
      allLink.classList.add('draggable-blog-filter__link--active');
    }

    this.selectedBlog = null;
  }

  /**
   * Handle window resize
   */
  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 750;

    if (wasMobile !== this.isMobile) {
      if (this.currentView === 'cards') {
        if (!this.isMobile) {
          // Moved to desktop - enable dragging, disable mobile swipe
          this.disableMobileSwipe();
          this.initializeCardPositions();
          this.enableDragging();
        } else {
          // Moved to mobile - disable dragging, enable mobile swipe
          this.disableDragging();

          // Reset card positions and enable mobile stack
          this.draggableCards.forEach(card => {
            card.style.transform = '';
            card.style.zIndex = '';
          });

          this.initializeMobileStack();
          this.setupMobileSwipe();
        }
      }
    } else if (!this.isMobile && this.currentView === 'cards') {
      // Desktop resize - update container height
      const containerHeight = Math.max(800, this.draggableContainer.getBoundingClientRect().height);
      this.draggableContainer.style.minHeight = `${containerHeight}px`;
    }
  }

  /**
   * Cleanup
   */
  destroy() {
    console.log('[IssuesShowcase] Destroying instance...');

    // Cover card images are handled by direct links - no listeners to remove

    // Remove back button listener
    if (this.backButton) {
      this.backButton.removeEventListener('click', this.boundHandlers.backClick);
    }

    // Remove resize listener
    window.removeEventListener('resize', this.boundHandlers.resize);

    // Disable dragging and mobile swipe
    this.disableDragging();
    this.disableMobileSwipe();

    // Clear references
    this.container = null;
    this.coversView = null;
    this.cardsView = null;
    this.backButton = null;
    this.coverCards = null;
    this.draggableCards = null;
    this.draggableContainer = null;
    this.cardCounter = null;
    this.mobileStack = [];
  }
}

// Store instances
const showcaseInstances = new WeakMap();

// Initialize
function initializeIssuesShowcase(container) {
  console.log('[IssuesShowcase] Starting initialization for container...');

  // Check if already initialized
  if (showcaseInstances.has(container)) {
    console.log('[IssuesShowcase] Container already initialized, destroying previous instance');
    const existingInstance = showcaseInstances.get(container);
    existingInstance.destroy();
  }

  // Create new instance
  const instance = new IssuesShowcase(container);
  showcaseInstances.set(container, instance);

  console.log('[IssuesShowcase] Initialization complete');
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.issues-showcase');
  console.log(`[IssuesShowcase] Found ${containers.length} container(s) to initialize`);

  containers.forEach(container => {
    initializeIssuesShowcase(container);
  });
});

// Shopify theme editor support
document.addEventListener('shopify:section:load', (event) => {
  const container = event.target.querySelector('.issues-showcase');
  if (container) {
    setTimeout(() => {
      initializeIssuesShowcase(container);
    }, 0);
  }
});

document.addEventListener('shopify:section:select', (event) => {
  const container = event.target.querySelector('.issues-showcase');
  if (container) {
    setTimeout(() => {
      initializeIssuesShowcase(container);
    }, 0);
  }
});
